/**
 * Décision 372 — « Demander une vérification à la chargée ». L'accueil trouve
 * une personne non ayant droit (salarié inactif, retiré ou parti, ayant droit
 * non couvert, entreprise suspendue ou résiliée) qui affirme être couverte :
 * il demande à la chargée de vérifier, en écrivant ce que dit la personne.
 * L'accueil ne décide rien. Décision 402 : seulement pour un écart DNS ;
 * âge, suspension et résiliation sont des décisions fermes. Décision 399 : ce
 * que la chargée tranche vaut jusqu'à ce qu'on l'infirme, jamais
 * « aujourd'hui seulement » ; la demande d'un ayant droit refusé à cause de
 * son salarié est portée par le salarié.
 */
import { estMotifDuDns } from './famille-reactivation.js';
import { LIBELLE_MOTIF_NON_ELIGIBLE, MOTIFS_NON_ELIGIBLE, } from './eligibilite.js';
/** Ce que l'accueil écrit de ce que dit la personne. */
export const NOTE_VERIFICATION_MAX = 300;
/** Le motif de « Ne peut pas entrer », dit par la chargée. */
export const MOTIF_REFUS_VERIFICATION_MAX = 300;
/** Décision 399 — la fenêtre de l'accueil : un seul champ, obligatoire. */
export const LIBELLE_NOTE_VERIFICATION = 'Ce que dit la personne, pour la chargée';
export const REPONSES_VERIFICATION = ['PEUT_ENTRER', 'NE_PEUT_PAS_ENTRER'];
/**
 * Décision 402 — la vérification ne sert qu'aux écarts DNS : une personne
 * sortie de la liste active par un DNS (absente, retirée, déclarée partie en
 * couleur ou par une date), ou dont on ne sait pas ce qui l'a sortie (la
 * plateforme la dit « absente du dernier DNS ») ; et l'ayant droit dont le
 * salarié est dans ce cas. L'âge, la suspension, la résiliation, et les
 * gestes de la chargée elle-même (départ ou retrait déclarés à la main), sont
 * des décisions fermes : la raison s'affiche, sans demande possible.
 * Décision 409 (M1) : l'infirmation par la chargée en est une aussi.
 */
export function estEcartDns(c) {
    switch (c.motif) {
        case 'ABSENT_DU_DNS':
            // Décision 409 (M1) — infirmer est une décision de la chargée, pas un
            // écart DNS, même si la fiche garde le motif « absent du dernier DNS ».
            if (c.infirmee === true)
                return false;
            return c.motifInactivite === null || estMotifDuDns(c.motifInactivite);
        case 'TITULAIRE_NON_ELIGIBLE':
            return c.ecartDuSalarie === true;
        default:
            return false;
    }
}
const MOIS = [
    'janvier',
    'février',
    'mars',
    'avril',
    'mai',
    'juin',
    'juillet',
    'août',
    'septembre',
    'octobre',
    'novembre',
    'décembre',
];
/** « mars 2026 », d'après un jour civil AAAA-MM-JJ. */
export function moisEnLettres(jour) {
    const mois = MOIS[Number(jour.slice(5, 7)) - 1] ?? '';
    return `${mois} ${jour.slice(0, 4)}`;
}
const depuisLe = (jour) => jour === null ? '' : ` en ${moisEnLettres(jour)}`;
/** « Retiré par le DNS … » : la sortie de la liste active, dite comme l'accueil l'explique. */
function sortieDeLaListe(c) {
    if (c.infirmee === true) {
        return `Fiche infirmée par la chargée de clientèle${depuisLe(c.inactifDepuis)}`;
    }
    switch (c.motifInactivite) {
        case 'ABSENT_DU_DERNIER_DNS':
            return `Retiré par le DNS de son entreprise${depuisLe(c.inactifDepuis)}`;
        case 'DEPART_DECLARE_COULEUR':
        case 'DEPART_DECLARE_DATE':
            return `Déclaré parti dans le DNS de son entreprise${depuisLe(c.inactifDepuis)}`;
        case 'DESACTIVATION_MANUELLE':
            return `Désactivé par la chargée de clientèle${depuisLe(c.inactifDepuis)}`;
        case 'ADHERENT_RESILIE':
            return 'Désactivé à la fin de l’adhésion de son entreprise';
        default:
            return c.ayantDroit
                ? 'Ayant droit désactivé : plus sur la liste active'
                : 'Absent du dernier DNS de son entreprise';
    }
}
/**
 * La raison d'inéligibilité en une phrase courte, pour l'accueil et la
 * chargée : « Entreprise suspendue », « Retiré par le DNS de son entreprise
 * en mars 2026 ». Le message complet du verdict reste sur la fiche.
 */
export function raisonDeNonEligibilite(c) {
    switch (c.motif) {
        case 'ADHERENT_SUSPENDU':
            return 'Entreprise suspendue';
        case 'ADHERENT_RESILIE':
            return 'Adhésion de l’entreprise terminée';
        case 'ABSENT_DU_DNS':
            return sortieDeLaListe(c);
        case 'TITULAIRE_NON_ELIGIBLE':
            return c.raisonDuTitulaire
                ? `Son salarié n’est plus couvert : ${c.raisonDuTitulaire.charAt(0).toLowerCase()}${c.raisonDuTitulaire.slice(1)}`
                : 'Son salarié n’est plus couvert';
        case 'AYANT_DROIT_RETIRE':
            return `Retiré de la couverture de son salarié par la chargée${depuisLe(c.inactifDepuis)}`;
        case 'SALARIE_PARTI':
            return `Déclaré parti par la chargée, sur courrier de l’entreprise${depuisLe(c.inactifDepuis)}`;
        case 'ENFANT_AU_DELA_DE_L_AGE':
            return `Enfant de plus de ${String(c.ageLimiteEnfant ?? 21)} ans : plus couvert par son parent`;
        case 'EN_ATTENTE_DE_CONFIRMATION':
            return 'Fiche en attente de confirmation par la chargée';
        case 'CLIENT_EXTERNE':
            return 'Client externe';
        case 'CONTRAT_INCONNU':
            return 'Dossier rattaché à aucun contrat';
    }
}
/**
 * Décision 399 — ce que fait la réponse de la chargée, selon la cause du
 * refus lue au moment de répondre. Rien ne vaut « aujourd'hui seulement ».
 * - `REACTIVER` : un écart DNS ; la personne redevient active et confirmée
 *   sur son contrat (377), sa famille suit (390), jusqu'au prochain DNS.
 * - `PAR_SON_SALARIE` : un ayant droit refusé à cause de son salarié ; la
 *   demande est portée par le salarié, c'est lui qu'elle tranche.
 * - `DEJA_COUVERT` : la cause a été levée entre-temps ; « Peut entrer »
 *   prévient seulement l'accueil.
 * - `REFUSER_SEULEMENT` : la cause est devenue une décision ferme (décision
 *   402 : âge, suspension, résiliation…) ; seulement « Ne peut pas entrer ».
 * - `COUVERT_PAR_LE_DNS` : décision 405 — la demande s'est fermée d'elle-même,
 *   la personne étant de nouveau couverte (un DNS, ou un autre geste) avant
 *   toute réponse. Ce que l'accueil lit, jamais un bouton.
 */
export const ACTIONS_VERIFICATION = [
    'REACTIVER',
    'PAR_SON_SALARIE',
    'DEJA_COUVERT',
    'REFUSER_SEULEMENT',
    'COUVERT_PAR_LE_DNS',
];
/** L'action d'un refus (`motif` `null` : la personne est couverte). */
export function actionDeLaVerification(c) {
    if (c.motif === null)
        return 'DEJA_COUVERT';
    if (!estEcartDns(c))
        return 'REFUSER_SEULEMENT';
    return c.motif === 'TITULAIRE_NON_ELIGIBLE' ? 'PAR_SON_SALARIE' : 'REACTIVER';
}
/**
 * L'action d'une demande d'après le seul refus gardé à la demande : ce que
 * l'accueil lit après la réponse. Les causes que la chargée réactivait avant
 * la décision 402 (départ ou retrait à la main) restent « réactivé ».
 */
export function actionDeLaDemande(motif, 
/** Décision 405 — acceptée sans réponse de la chargée : fermée d'elle-même. */
fermeeDElleMeme = false) {
    if (fermeeDElleMeme)
        return 'COUVERT_PAR_LE_DNS';
    switch (motif) {
        case 'ABSENT_DU_DNS':
        case 'SALARIE_PARTI':
        case 'AYANT_DROIT_RETIRE':
            return 'REACTIVER';
        case 'TITULAIRE_NON_ELIGIBLE':
            return 'PAR_SON_SALARIE';
        default:
            return 'REFUSER_SEULEMENT';
    }
}
/**
 * Décision 399 — celui que la chargée lit et tranche : le salarié quand
 * l'ayant droit n'est refusé qu'à cause de lui, sinon la personne elle-même.
 */
export function porteurDeLaDemande(d) {
    return d.motif === 'TITULAIRE_NON_ELIGIBLE' && d.titulaireId !== null
        ? d.titulaireId
        : d.personneId;
}
/** Le bouton « oui » de la chargée dit l'effet avant le clic ; `null` : refus seulement. */
export const BOUTON_PEUT_ENTRER = {
    REACTIVER: 'Peut entrer : réactiver le dossier',
    PAR_SON_SALARIE: null,
    DEJA_COUVERT: 'Peut entrer',
    REFUSER_SEULEMENT: null,
    COUVERT_PAR_LE_DNS: null,
};
/** La phrase sous les boutons : ce que la réponse change, et jusqu'à quand. */
export const EFFET_DE_LA_REPONSE = {
    REACTIVER: 'Le dossier redevient actif et confirmé sur son contrat, avec sa famille. Le prochain DNS de l’entreprise fera foi.',
    PAR_SON_SALARIE: 'La demande est portée par son salarié.',
    DEJA_COUVERT: 'La personne est déjà couverte de nouveau : la réponse prévient l’accueil.',
    REFUSER_SEULEMENT: 'Ce refus n’est pas un écart DNS : seule la réponse « Ne peut pas entrer » est possible.',
    COUVERT_PAR_LE_DNS: 'De nouveau couverte avant la réponse : la demande s’est fermée d’elle-même.',
};
/** Ce que l'accueil lit après « Peut entrer ». */
export const LIBELLE_ADMISSION = {
    REACTIVER: 'Dossier réactivé par la chargée',
    PAR_SON_SALARIE: 'Son salarié est validé par la chargée',
    DEJA_COUVERT: 'Admis par la chargée',
    // Ne s'affiche que pour une ancienne réponse « aujourd'hui seulement »
    // (entreprise suspendue, âge…), d'avant les décisions 399 et 402.
    REFUSER_SEULEMENT: 'Admis par la chargée',
    COUVERT_PAR_LE_DNS: 'Couvert par le DNS',
};
/** Le libellé court d'un motif lu en base (registre), ou `null` s'il est inconnu. */
export function libelleDuMotif(motif) {
    const connu = MOTIFS_NON_ELIGIBLE.find((m) => m === motif);
    return connu === undefined ? null : LIBELLE_MOTIF_NON_ELIGIBLE[connu];
}
//# sourceMappingURL=verification-chargee.js.map